import { useState, type DragEvent } from 'react';

import type { RefreshIntervalMs } from './snapshots-model';

import type { SnapshotDto } from '../../api/snapshots';
import {
  SnapshotRefreshError,
  SnapshotUploadError,
  useSnapshotRefresh,
  useSnapshotUpload,
  useSnapshots,
} from '../../api/snapshots';
import { useAuth } from '../../auth/AuthContext';
import { hasAtLeast } from '../../auth/roles';
import { Badge, EmptyState, ErrorState } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { formatDateTime, formatDateTimeMsk } from '../dashboard/format';

import {
  APPLY_HINTS,
  APPLY_NONE_HINT,
  APPLY_STALL_HINT,
  DEFAULT_APPLY_STALE_MS,
  JOURNAL_ACCEPTED_HINT,
  JOURNAL_TOTAL_HINT,
  REFRESH_OPTIONS,
  UPLOAD_ACCEPT,
  applyDetail,
  applyLabel,
  applyStateOf,
  applyStall,
  applyTone,
  firstUploadFile,
  healthRows,
  healthTone,
  ingestErrorOf,
  kindLabel,
  loadRefreshIntervalMs,
  parseRefreshIntervalMs,
  parseSnapshotList,
  pipelinePollInterval,
  saveRefreshIntervalMs,
  snapshotCounts,
  sourceLabel,
  uploadTypeError,
} from './snapshots-model';

import './snapshots.css';

/**
 * The «Снапшоты» tab of Settings (И5, task T.C4b) — the ingest journal, the
 * per-source freshness badges and the manual upload (contract #19/#20,
 * ADMIN-only).
 *
 * Lives here since the tab merge: it used to be `/quotas?view=snapshots`, and the
 * move is a pure relocation — the component, its model and its styles travel
 * together, the testids and the api calls are untouched, so `/quotas` is back to
 * being only the grid and the owner keeps one admin surface instead of two.
 *
 * CONTRACT-FIRST: the e2e stubs these endpoints (`page.route`) with the exact
 * shapes of `api/snapshots.ts` — including the 409 DUPLICATE_SNAPSHOT with its
 * `existingSnapshotId`. Nothing in here knows or cares whether the answer came
 * from the api or from a route stub.
 *
 * Contract #23 (the full SystemStatus panel) is deliberately NOT built here: the
 * badges read only the `sourcesHealth` block of #20, and the freshness threshold
 * is this tab's own presentation choice.
 */
export function SnapshotsTab() {
  const { role } = useAuth();
  // The whole tab is ADMIN (the api answers 403 to everyone else on #19/#20), so
  // the refresh button is gated to the same minimum — showing it to a MANAGER
  // would be a button that can only answer 403.
  const canRefresh = hasAtLeast(role, 'ADMIN');

  // «Автообновление» is per browser and OFF until the admin asks for it. The
  // pipeline poll (T.Z40) is the one exception: while the payload says an apply
  // is queued, the tab watches for the run to finish at a light cadence.
  const [refreshMs, setRefreshMs] = useState<RefreshIntervalMs>(() => loadRefreshIntervalMs(window.localStorage));
  const journal = useSnapshots(refreshMs, pipelinePollInterval);

  // Frozen per render: the badges compare ages, so a value taken while the
  // payload is on screen is exactly what the label describes.
  const now = Date.now();
  const [lastUpload, setLastUpload] = useState<SnapshotDto | null>(null);

  const data = parseSnapshotList(journal.data);
  const snapshots = data.snapshots;
  // The api publishes its own stall threshold; an older api means the documented default.
  const staleAfterMs = data.applyStaleAfterMs ?? DEFAULT_APPLY_STALE_MS;

  const pickRefreshMs = (value: RefreshIntervalMs) => {
    setRefreshMs(value);
    saveRefreshIntervalMs(window.localStorage, value);
  };

  return (
    <>
      <section className="health" aria-label="Свежесть источников" data-testid="sources-health">
        {healthRows(data.sourcesHealth).map((row) => {
          const tone = healthTone(row.lastAt, now);
          return (
            <div className="health__item" key={row.key} data-testid={`health-${row.key}`} data-tone={tone}>
              <span className="health__label">
                {row.label} <span className="health__hint">{row.hint}</span>
              </span>
              <span className="health__value" data-testid={`health-${row.key}-at`}>
                {formatDateTime(row.lastAt)}
              </span>
              <span className="health__badge">
                <Badge tone={tone} title={HEALTH_HINT[tone]}>
                  {HEALTH_LABEL[tone]}
                </Badge>
              </span>
            </div>
          );
        })}
      </section>

      <UploadCard onUploaded={setLastUpload} lastUpload={lastUpload} />

      <div className="card card--flush">
        <div className="settings-toolbar">
          <h2 className="settings-toolbar__title">Журнал снапшотов</h2>
          <p className="settings-toolbar__meta" data-testid="journal-meta">
            {snapshots.length} снапшотов · новые сверху
          </p>
          <div className="journal-controls">
            {canRefresh ? <RefreshButton /> : null}
            <label className="journal-controls__field">
              <span className="journal-controls__label">Автообновление</span>
              <select
                className="input"
                data-testid="journal-refresh-select"
                value={refreshMs}
                onChange={(event) => pickRefreshMs(parseRefreshIntervalMs(Number(event.target.value)))}
              >
                {REFRESH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {journal.isPending ? (
          <JournalSkeleton />
        ) : journal.isError ? (
          <div className="snapshots-state">
            <ErrorState
              message={journal.error instanceof Error ? journal.error.message : 'Не удалось загрузить журнал.'}
              onRetry={() => void journal.refetch()}
            />
          </div>
        ) : snapshots.length === 0 ? (
          <EmptyState
            title="Снапшотов ещё не было"
            hint="Загрузите .7z или .xlsx вручную — или дождитесь письма от УНТИ."
          />
        ) : (
          <>
            {/* T.Z39: the owner's «Всего 5504, а на дашборде ~4496» — answered on the
                page where the file's number lives, not only in a header tooltip. */}
            <p className="journal-hint" data-testid="journal-hint">
              {JOURNAL_TOTAL_HINT}
            </p>
            <TableWrap>
              <table className="data" data-testid="journal-table">
                <thead>
                  <tr>
                    <th scope="col">Тип</th>
                    <th scope="col">Источник</th>
                    <th scope="col">Файл</th>
                    <th scope="col">Дата выгрузки</th>
                    <th scope="col" className="num" title={JOURNAL_TOTAL_HINT}>
                      Всего
                    </th>
                    <th scope="col" className="num" title={JOURNAL_ACCEPTED_HINT}>
                      Принято
                    </th>
                    <th scope="col" className="num">
                      Карантин
                    </th>
                    <th scope="col">Ошибка загрузки</th>
                    <th scope="col">Конвейер</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshots.map((snapshot) => {
                    const counts = snapshotCounts(snapshot);
                    const ingestError = ingestErrorOf(snapshot);
                    const apply = applyStateOf(snapshot);
                    const applyToneOf = applyTone(apply);
                    const applyDetailLine = apply === null ? null : applyDetail(apply);
                    // T.Z40 — the alarm: fresh snapshot, apply not landed in time.
                    const stalled = applyStall(snapshot, staleAfterMs, now);
                    return (
                      <tr key={snapshot.id} data-testid="journal-row" data-snapshot-id={snapshot.id}>
                        <td data-testid="journal-kind">
                          <Badge tone={snapshot.kind === 'EVI' ? 'brand' : 'neutral'}>{kindLabel(snapshot.kind)}</Badge>
                        </td>
                        <td data-testid="journal-source">{sourceLabel(snapshot.source)}</td>
                        <td className="journal-file" data-testid="journal-file" title={snapshot.fileName}>
                          {snapshot.fileName}
                        </td>
                        <td data-testid="journal-export-date">{formatDateTime(snapshot.exportDate)}</td>
                        <td className="num" data-testid="journal-rows-total">
                          {counts.total}
                        </td>
                        <td className="num" data-testid="journal-rows-accepted">
                          {counts.accepted}
                        </td>
                        <td className="num" data-testid="journal-rows-quarantine">
                          {counts.quarantined}
                        </td>
                        <td className={ingestError ? 'journal-error' : 'cell-muted'} data-testid="journal-ingest-error">
                          {ingestError ?? '—'}
                        </td>
                        <td
                          className="journal-apply"
                          data-testid="journal-apply"
                          data-apply-state={apply?.state ?? 'none'}
                          data-stalled={stalled !== null ? 'true' : undefined}
                        >
                          <span className="journal-apply__received" data-testid="journal-apply-received">
                            получено {formatDateTimeMsk(snapshot.receivedAt)}
                          </span>
                          {stalled !== null ? (
                            // The alarm replaces the state badge: «queued» was its calm
                            // wording, this is the same fact escalated to danger.
                            <Badge tone="bad" title={APPLY_STALL_HINT} testId="journal-apply-stall">
                              {stalled.label}
                            </Badge>
                          ) : apply === null ? (
                            <span className="cell-muted" title={APPLY_NONE_HINT}>
                              —
                            </span>
                          ) : (
                            <>
                              <Badge tone={applyToneOf} title={APPLY_HINTS[apply.state]} testId="journal-apply-badge">
                                {applyLabel(apply)}
                              </Badge>
                              {applyDetailLine !== null ? (
                                <span className="journal-apply__detail" data-testid="journal-apply-detail">
                                  {applyDetailLine}
                                </span>
                              ) : null}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableWrap>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// «Обновить данные» (ADDITIVE v1.6.1)
// ---------------------------------------------------------------------------

/**
 * Asks the worker to read the mailbox again. The 202 says the request is QUEUED,
 * not that new letters landed — so the honest label is «Проверяем почту…» while
 * the request is in flight, and «Проверили почту — новых снапшотов нет» after it:
 * the journal refetch is the button's own doing, anything new in it is the
 * worker's answer, and saying more would be promising a poll we did not run.
 */
function RefreshButton() {
  const refresh = useSnapshotRefresh();
  const error = refresh.error instanceof SnapshotRefreshError ? refresh.error : null;

  return (
    <div className="journal-refresh">
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        data-testid="journal-refresh-button"
        disabled={refresh.isPending}
        onClick={() => refresh.mutate()}
      >
        Обновить данные
      </button>

      {refresh.isPending ? (
        <span className="journal-refresh__status" data-testid="journal-refresh-status" role="status">
          Проверяем почту…
        </span>
      ) : refresh.isSuccess ? (
        <span className="journal-refresh__status" data-testid="journal-refresh-done" role="status">
          Проверили почту{refresh.data?.requested === false ? ' — запрос не принят' : ''}
        </span>
      ) : error ? (
        <span className="journal-refresh__error" data-testid="journal-refresh-error" role="alert">
          {error.status === 403 ? 'Обновление доступно только администратору.' : error.message}
        </span>
      ) : null}
    </div>
  );
}

const HEALTH_LABEL = { ok: 'свежий', warn: 'давно', bad: 'нет данных' } as const;
const HEALTH_HINT = {
  ok: 'Источник отчитался за последние 7 дней',
  warn: 'Источник молчит больше недели',
  bad: 'Источник ещё не отчитался',
} as const;

// ---------------------------------------------------------------------------
// The upload (contract #19)
// ---------------------------------------------------------------------------

function UploadCard({
  onUploaded,
  lastUpload,
}: {
  onUploaded: (snapshot: SnapshotDto) => void;
  lastUpload: SnapshotDto | null;
}) {
  const upload = useSnapshotUpload();
  const [dragOver, setDragOver] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [fileLabel, setFileLabel] = useState<string | null>(null);

  const send = (file: File | undefined) => {
    const typeError = uploadTypeError(file);
    if (typeError !== undefined) {
      setClientError(typeError);
      return;
    }
    setClientError(null);
    if (!file) return;
    upload.mutate(file, {
      onSuccess: (snapshot) => {
        setFileLabel(file.name);
        onUploaded(snapshot);
      },
    });
  };

  // The 409 keeps `existingSnapshotId`, which the shared ApiError would drop —
  // that is why the upload throws its own error type (see api/snapshots.ts).
  const serverError = upload.error instanceof SnapshotUploadError ? upload.error : null;

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    send(firstUploadFile(event.dataTransfer?.files));
  };

  return (
    <div className="card">
      <div className="upload">
        <div
          className={dragOver ? 'upload__zone upload__zone--over' : 'upload__zone'}
          data-testid="upload-zone"
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <p className="upload__title">Перетащите файл сюда</p>
          <p className="upload__hint">.7z (ЕПГУ/ЕВИ) или .xlsx — дальше тот же конвейер, что у писем УНТИ</p>
          <label className="btn btn--primary btn--sm upload__button">
            <input
              type="file"
              accept={UPLOAD_ACCEPT}
              data-testid="upload-input"
              className="upload__input"
              disabled={upload.isPending}
              onChange={(event) => {
                send(firstUploadFile(event.target.files));
                event.target.value = '';
              }}
            />
            Выбрать файл
          </label>
        </div>

        <div className="upload__result">
          {upload.isPending ? (
            <p className="upload__status" data-testid="upload-status" role="status">
              Загружаем и разбираем файл…
            </p>
          ) : null}

          {serverError ? (
            <p className="upload__error" role="alert" data-testid="upload-error">
              {serverError.is('DUPLICATE_SNAPSHOT', 409) && serverError.existingSnapshotId !== undefined ? (
                // The id is the whole point of this error: the file's rows are
                // already in the database under that snapshot, nothing to redo.
                <>
                  Такой файл уже загружен — это снапшот{' '}
                  <strong data-testid="upload-error-snapshot-id">№{serverError.existingSnapshotId}</strong> в журнале
                  ниже. Повторная загрузка не нужна.
                </>
              ) : (
                serverError.message
              )}
            </p>
          ) : null}

          {clientError ? (
            <p className="upload__error" role="alert" data-testid="upload-type-error">
              {clientError}
            </p>
          ) : null}

          {lastUpload ? (
            <div className="upload__ok" data-testid="upload-result">
              <p className="upload__ok-title">
                Снапшот №{lastUpload.id} ({kindLabel(lastUpload.kind)}, {sourceLabel(lastUpload.source)}) записан
                {fileLabel ? ` — ${fileLabel}` : ''}.
              </p>
              <p className="upload__ok-line" data-testid="upload-result-counts">
                Принято {snapshotCounts(lastUpload).accepted} из {snapshotCounts(lastUpload).total}
                {snapshotCounts(lastUpload).quarantined > 0
                  ? `, в карантине ${snapshotCounts(lastUpload).quarantined}`
                  : ''}
                .
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function JournalSkeleton() {
  return (
    <div className="snapshots-state" data-testid="journal-skeleton">
      <div className="skeleton skeleton--line skeleton--wide" />
      <div className="skeleton skeleton--line" />
      <div className="skeleton skeleton--line" />
    </div>
  );
}
